import React, { useContext, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import {
  BASE_PURCHASE_SUBSCRIPTION_SETTINGS_PATH,
  BILLING_SETTINGS_PATH,
  MY_GROUPS_PATH,
  TRADE_EXTRA_DETAILS_TABS,
  UPGRADE_SUBSCRIPTION_SETTINGS_PATH,
  VERY_SMALL_MOBILE,
} from "common/constants";
import { formatDateRelative } from "@shared/utils/date";
import Flex from "common/Boxes/Flex";
import { StatusCircle } from "Global/StatusCircle";
import { useHistory, useLocation } from "react-router-dom";
import {
  getAccountExtraDetailsRoute,
  getBotExtraDetailsRoute,
  getTradeExtraDetailsRoute,
} from "utils/routes";
import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import { FixMeLater } from "types";
import useMeasure from "utils/hooks/useMeasure";
import { AvoContext } from "analytics/AvoProvider";
import { typography } from "@shared/common/Themes/typography";
import { Notification, NotificationScreens } from "@shared/types";
import {
  NotificationPartClicked,
  NotificationPartClickedValueType,
} from "analytics/Avo";
import AntCaretDownOutlined from "@ant-design/icons/CaretDownOutlined";
import AntCaretRightOutlined from "@ant-design/icons/CaretRightOutlined";
import ShareAltOutlined from "@ant-design/icons/ShareAltOutlined";
import { IconWithSource } from "features/trades/common/IconWithSource";
import ExpandCollapse from "common/Animations/ExpandCollapse";
import Timeline from "common/Timeline/Timeline";
import useNotification from "@shared/features/notifications/useNotification";
import { BotExtraDetailsTabs } from "@shared/features/bots/types";
import { useIntercom } from "react-use-intercom";
import CornerStarBanner from "common/misc/CornerStarBanner";
import Link from "common/Typography/Link";
import useMedia from "utils/hooks/useMedia";
import ShareTradeTrigger from "features/trades/common/ShareTradeTrigger";
import { useShareTradeResults } from "@shared/features/trades/common/hooks";
import { displayResultMessage, openMessage } from "utils/misc";
import Spin from "antd/es/spin";
import { useUser } from "@shared/utils/hooks/misc.ts";

interface NotificationItemProps {
  notification: Notification[];
  setItemHeight: (height: number) => void;
  style?: object;
  showUpgrade?: boolean;
  closeModal?: () => void;
  bannerImageLink?: string;
}

const NotificationItem: React.FunctionComponent<NotificationItemProps> = ({
  notification,
  setItemHeight,
  style,
  showUpgrade,
  closeModal,
  bannerImageLink,
}) => {
  const { avo } = useContext(AvoContext);
  const isMobile = useMedia(
    [`(max-width: ${VERY_SMALL_MOBILE})`],
    [true],
    false
  );
  const { show: showIntercom } = useIntercom();
  const location = useLocation();
  const history = useHistory();
  const [measureRef, { height }] = useMeasure();
  const [expandedTimeline, setExpandedTimeline] = useState(false);
  const [expandedSingleNotification, setExpandedSingleNotification] =
    useState(false);
  const { user } = useUser();

  const {
    lastNotificationItem,
    isAggregateNotification,
    showBadge,
    clickable,
    showButton,
    buttonText,
    markUnreadNotificationsAsRead,
    delayedExpandedTimeline,
    getTimelineItemColor,
    isLongNotification,
    getSingleNotificationSubMessage,
  } = useNotification(
    notification,
    isMobile ? 38 : 48,
    expandedTimeline,
    expandedSingleNotification,
    70
  );

  const {
    isShareResultsModalVisible,
    shareTradeImageUrl,
    shareTradeImageLoading,
    openShareResultsModal,
    closeShareResultsModal,
    imageTitle,
  } = useShareTradeResults(displayResultMessage, openMessage);

  useEffect(() => {
    setItemHeight(height);
  }, [height]);

  const sendAvoEvent = (clickedItem: NotificationPartClickedValueType) => {
    avo?.singleNotificationClicked({
      notificationType: lastNotificationItem.type,
      notificationPartClicked: clickedItem,
    });
  };

  const openExtraDetailsDrawer = (
    getExtraDetailsRoute: (
      location: string,
      id: number,
      tab?: string
    ) => string,
    tabNames?: string[]
  ) => {
    const tab =
      tabNames?.[
        lastNotificationItem?.tab_number !== null
          ? lastNotificationItem?.tab_number
          : 0
      ];

    history.push(
      getExtraDetailsRoute(
        location.pathname,
        lastNotificationItem.component_id,
        tab
      )
    );
  };

  const handleClick = () => {
    const screen = lastNotificationItem?.screen;
    if (screen === NotificationScreens.TRADE_DETAIL) {
      openExtraDetailsDrawer(
        getTradeExtraDetailsRoute,
        Object.values(TRADE_EXTRA_DETAILS_TABS)
      );
    } else if (
      screen === NotificationScreens.ACCOUNT_FORM ||
      screen === NotificationScreens.ACCOUNT_DETAIL
    ) {
      openExtraDetailsDrawer(getAccountExtraDetailsRoute);
    } else if (screen === NotificationScreens.BOT_DETAIL) {
      openExtraDetailsDrawer(
        getBotExtraDetailsRoute,
        Object.values(BotExtraDetailsTabs)
      );
    } else if (screen === NotificationScreens.GROUPS) {
      history.push(MY_GROUPS_PATH);
    } else if (screen === NotificationScreens.SUBSCRIPTION) {
      history.push(BILLING_SETTINGS_PATH);
    }
  };

  const onNotificationClick = () => {
    if (clickable) {
      markUnreadNotificationsAsRead();
      if (isAggregateNotification) {
        setExpandedTimeline(!expandedTimeline);
      } else if (isLongNotification) {
        setExpandedSingleNotification(!expandedSingleNotification);
      } else {
        sendAvoEvent(NotificationPartClicked.NOTIFICATION_BOX);
        handleClick();
      }
    }
  };

  const onButtonClick = (e) => {
    e.stopPropagation();
    sendAvoEvent(NotificationPartClicked.NOTIFICATION_C2A_BUTTON);
    if (buttonText?.toLowerCase() === "contact support") {
      showIntercom();
    } else if (lastNotificationItem?.button_link) {
      window.open(lastNotificationItem.button_link, "_blank");
    } else {
      handleClick();
    }
  };

  const icon = (
    <IconWithSource
      mainIconSize={ICON_SIZE}
      mainImageLink={lastNotificationItem?.main_icon_link}
      sourceImageLink={lastNotificationItem.type_icon_link}
    />
  );

  const getExpandCollapseIcon = (expanded: boolean) =>
    expanded ? <CaretDownOutlined /> : <CaretRightOutlined />;

  const getTimelineItem = (subItem: Notification, isLastSubItem?: boolean) => {
    return {
      color: getTimelineItemColor(subItem),
      children: (
        <Flex $alignItems="flex-start" style={{ width: "100%" }}>
          <div>
            <Message>{subItem.message}</Message>
            <SubMessage>{subItem.sub_message}</SubMessage>
          </div>
          {isLastSubItem ? (
            getExpandCollapseIcon(expandedTimeline)
          ) : (
            <Date>{formatDateRelative(subItem.time, true, true)}</Date>
          )}
        </Flex>
      ),
    };
  };

  const collapsedTimelineItems = notification.slice(1);
  const timelineItems = [getTimelineItem(lastNotificationItem, true)];
  let expandedTimelineItems = collapsedTimelineItems.map((subItem) =>
    getTimelineItem(subItem)
  );

  const timelineNotification = (
    <div style={{ marginTop: 12 }}>
      <MainItemTimeline
        items={timelineItems}
        $expanded={delayedExpandedTimeline}
      />
      <ExpandCollapse expanded={expandedTimeline}>
        <ExpandedTimeline items={expandedTimelineItems} />
      </ExpandCollapse>
    </div>
  );

  const subMessageData = getSingleNotificationSubMessage();

  const singleNotification = isLongNotification ? (
    <>
      <Flex $alignItems="flex-start">
        <Message>{lastNotificationItem.message}</Message>
        {getExpandCollapseIcon(expandedSingleNotification)}
      </Flex>
      <SubMessage>{subMessageData?.subMessage}</SubMessage>
      <ExpandCollapse expanded={expandedSingleNotification}>
        <SubMessage>{subMessageData?.collapsedSubMessage}</SubMessage>
      </ExpandCollapse>
    </>
  ) : (
    <div>
      <Message>{lastNotificationItem.message}</Message>
      <SubMessage>{subMessageData?.subMessage}</SubMessage>
    </div>
  );

  const button = showButton && buttonText && (
    <NotificationButton onClick={onButtonClick}>
      <Flex $justifyContent="flex-start">
        {buttonText}
        {lastNotificationItem?.button_icon_link && (
          <ButtonIcon src={lastNotificationItem?.button_icon_link} />
        )}
      </Flex>
    </NotificationButton>
  );

  const bannerImage = (
    <BannerWrapper>
      <BannerImage src={bannerImageLink} />
      <BannerText>
        <ShareAltOutlined style={{ marginRight: 8 }} />
        Share Result
        <StyledSpin spinning={shareTradeImageLoading} size="small" />
      </BannerText>
    </BannerWrapper>
  );

  const shareTradeBanner = bannerImageLink && (
    <ShareTradeTrigger
      trigger={bannerImage}
      visible={isShareResultsModalVisible}
      openModal={() => {
        openShareResultsModal(
          lastNotificationItem?.component_id,
          lastNotificationItem?.screen_header || "",
          undefined,
          undefined,
          closeModal
        );
      }}
      closeModal={closeShareResultsModal}
      shareTradeImageUrl={shareTradeImageUrl}
      imageTitle={imageTitle}
    />
  );

  const getUpgradeComponent = () => {
    if (!showUpgrade || user?.isPro()) return;
    return (
      <UpgradeWrapper>
        <CornerStarBanner />
        Don't miss another trade!{" "}
        <Link
          style={{ marginLeft: 8, fontWeight: 700 }}
          onClick={() => {
            history.push(
              user?.isFree()
                ? BASE_PURCHASE_SUBSCRIPTION_SETTINGS_PATH
                : UPGRADE_SUBSCRIPTION_SETTINGS_PATH
            );
            closeModal?.();
          }}
        >
          Upgrade Plan
        </Link>
      </UpgradeWrapper>
    );
  };

  return (
    <Wrapper style={style} $clickable={clickable}>
      <Flex $alignItems="start" style={{ width: "100%" }}>
        <ContentWrapper ref={measureRef as FixMeLater}>
          <IconContentWrapper onClick={onNotificationClick}>
            {icon}
            <Content>
              <TitleDateWrapper $showBadge={showBadge}>
                <Title level={5} $weight={typography.fontWeights.SEMI_BOLD}>
                  {lastNotificationItem.title}
                </Title>
                <Date>
                  {formatDateRelative(lastNotificationItem.time, true, true)}
                </Date>
              </TitleDateWrapper>
              {isAggregateNotification
                ? timelineNotification
                : singleNotification}
              {button}
            </Content>
          </IconContentWrapper>
          {shareTradeBanner}
          {getUpgradeComponent()}
        </ContentWrapper>
        {showBadge && (
          <BadgeWrapper>
            <StatusCircle />
          </BadgeWrapper>
        )}
      </Flex>
    </Wrapper>
  );
};

export default NotificationItem;

const ICON_SIZE = 28;

const fontStyle = css`
  font-size: ${(p) => p.theme.typography.fontSizes.S};
`;
const Wrapper = styled.div<{ $clickable: Boolean }>`
  border-bottom: ${(p) => p.theme.borders.lightest};
  padding: 16px 20px;
  display: flex;
  ${(p) => p.$clickable && `cursor: pointer;`}
`;
const BadgeWrapper = styled.div`
  position: absolute;
  top: 20px;
  right: 0;
`;
const ContentWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  align-items: center;
  flex: auto;
`;
const NotificationButton = styled(Link)`
  margin-top: 8px;
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  line-height: 28px;
  display: inline-block;
`;
const ButtonIcon = styled.img`
  width: 12px;
  height: auto;
  margin-left: 8px;
`;
const CaretStyle = css`
  color: ${(p) => p.theme.colors.secondaryGrey};
  padding-left: 8px;
`;
const CaretRightOutlined = styled(AntCaretRightOutlined)`
  ${CaretStyle}
`;
const CaretDownOutlined = styled(AntCaretDownOutlined)`
  ${CaretStyle}
`;
const TitleDateWrapper = styled(Flex)<{ $showBadge: boolean }>`
  margin-bottom: 6px;
  padding-right: ${(p) => (p.$showBadge ? "12px" : 0)};
  align-items: flex-start;
`;
const Date = styled(Text).attrs({ type: "secondary" })`
  ${fontStyle};
  min-width: 26px;
  display: flex;
  justify-content: flex-end;
`;
const Content = styled.div`
  width: 100%;
  margin-left: 6px;
`;
const IconContentWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
`;
const StyledTimeline = styled(Timeline)`
  width: 100%;
  .ant-timeline-item-content {
    position: relative;
    top: -4px;
    margin: 0 0 0 18px;
  }
  .ant-timeline-item-last > .ant-timeline-item-content {
    min-height: 0;
  }
  .ant-timeline-item {
    padding-bottom: 0;
  }
`;
const MainItemTimeline = styled(StyledTimeline)<{ $expanded?: boolean }>`
  ${(p) =>
    p.$expanded &&
    `
    .ant-timeline-item-last > .ant-timeline-item-tail {
    display: block;
    height: 130%;
  }
  `};
`;
const ExpandedTimeline = styled(StyledTimeline)`
  .ant-timeline-item {
    padding-top: 16px;
  }
  .ant-timeline-item-tail {
    height: 100%;
    inset-block-start: unset;
  }
`;
const Message = styled(Text)`
  ${fontStyle};
`;
const SubMessage = styled(Text)`
  color: ${(p) => p.theme.colors.secondaryGrey};
  line-height: 20px;
  ${fontStyle};
`;
const UpgradeWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(p) => (p.theme.isDarkMode ? "#5C6686" : "#EFF4FF")};
  position: relative;
  padding: 16px 20px;
  overflow: hidden;
  border-radius: 8px;
  margin-top: 16px;
  width: 100%;
  font-weight: 500;
`;
const BannerImage = styled.img`
  height: 60px;
  border-radius: 8px;
  object-fit: cover;
  z-index: 0;
  width: 100%;
`;
const BannerWrapper = styled.div`
  margin-top: 8px;
  width: 100%;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
`;
const BannerText = styled(Text)`
  color: ${(p) => p.theme.colors.white};
  font-size: ${(p) => p.theme.typography.fontSizes.L};
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  position: absolute;
  padding: 0 20px;
  z-index: 1;
`;
const StyledSpin = styled(Spin)`
  margin-left: 8px;
  color: ${(p) => p.theme.colors.white};
  .ant-spin-dot-item {
    background-color: ${(p) => p.theme.colors.white};
  }
`;
